import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// ── Keyframes
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

// ── Section
const Section = styled.section`
  position: relative;
  background: #ffffff;
  overflow: hidden;
  padding: 130px 0 150px;
`;

const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.03;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  pointer-events: none;
  opacity: ${({ op }) => op || 0.15};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  background: ${({ c }) => c};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
`;

const Container = styled.div`
  position: relative;
  z-index: 2;
  max-width: 780px;
  margin: 0 auto;
  padding: 0 clamp(1.5rem, 5vw, 4rem);
`;

// ── Header
const Header = styled.div`
  text-align: center;
  margin-bottom: 70px;
`;

const Eyebrow = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #a855f7;
  margin-bottom: 1.2rem;
  opacity: 0;
  transform: translateY(16px);
`;

const Title = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: clamp(2.4rem, 5vw, 4rem);
  font-weight: 300;
  line-height: 1.1;
  color: #535050;
  margin-bottom: 1rem;
  opacity: 0;
  transform: translateY(20px);

  em {
    font-style: italic;
    font-weight: 600;
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 5s linear infinite;
  }
`;

const Subtitle = styled.p`
  /* font-family: "DM Sans", sans-serif; */
  font-size: 0.95rem;
  font-weight: 300;
  color: rgb(151, 144, 144);
  opacity: 0;
  transform: translateY(16px);
`;

// ── FAQ list
const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  border-bottom: 1px solid rgba(168, 85, 247, 0.1);
  opacity: 0;
  transform: translateY(24px);

  &:first-of-type {
    border-top: 1px solid rgba(168, 85, 247, 0.1);
  }
`;

const Question = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 1.6rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  cursor: pointer;
  text-align: left;

  &:hover .q-text {
    color: rgba(34, 33, 33, 0.95);
  }
  &:hover .q-icon {
    border-color: rgba(168, 85, 247, 0.5);
    background: rgba(168, 85, 247, 0.08);
  }
`;

const QuestionText = styled.span`
  font-family: "Mendl", sans-serif;
  font-size: clamp(0.95rem, 1.5vw, 1.05rem);
  font-weight: 400;
  color: ${({ open }) => (open ? "#575353" : "rgb(92, 84, 84)")};
  line-height: 1.5;
  transition: color 0.2s ease;
  flex: 1;
`;

const IconWrap = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid
    ${({ open }) => (open ? "rgba(168,85,247,0.6)" : "rgba(255,255,255,0.12)")};
  background: ${({ open }) => (open ? "rgba(168,85,247,0.12)" : "transparent")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition:
    border-color 0.25s ease,
    background 0.25s ease,
    transform 0.3s ease;
  transform: ${({ open }) => (open ? "rotate(45deg)" : "rotate(0deg)")};
`;

const PlusIcon = styled.div`
  position: relative;
  width: 10px;
  height: 10px;

  &::before,
  &::after {
    content: "";
    position: absolute;
    background: ${({ open }) => (open ? "#c084fc" : "rgba(255,255,255,0.5)")};
    border-radius: 1px;
    transition: background 0.25s ease;
  }
  &::before {
    width: 10px;
    height: 1px;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
  }
  &::after {
    width: 1px;
    height: 10px;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
  }
`;

// Animated answer panel
const AnswerWrap = styled.div`
  overflow: hidden;
  height: 0;
`;

const Answer = styled.div`
  padding-bottom: 1.6rem;
  font-family: "Mendl", sans-serif;
  font-size: 0.92rem;
  font-weight: 300;
  color: rgb(172, 166, 166);
  line-height: 1.85;
  max-width: 640px;
`;

// ── Bottom CTA nudge
const BottomNote = styled.div`
  text-align: center;
  margin-top: 60px;
  opacity: 0;
  transform: translateY(20px);

  p {
    font-family: "Mendl", sans-serif;
    font-size: 0.88rem;
    font-weight: 300;
    color: rgb(71, 69, 69);
    margin-bottom: 0.6rem;
  }

  a {
    font-family: "Mendl", sans-serif;
    font-size: 0.82rem;
    font-weight: 500;
    color: #a855f7;
    text-decoration: none;
    letter-spacing: 0.05em;
    border-bottom: 1px solid rgba(168, 85, 247, 0.3);
    padding-bottom: 2px;
    transition:
      color 0.2s,
      border-color 0.2s;

    &:hover {
      color: #e879f9;
      border-color: rgba(232, 121, 249, 0.5);
    }
  }
`;

// ── Data
const FAQS = [
  // {
  //   q: "Is the app free to use?",
  //   a: "Yes — you can create a profile, browse, and match completely for free. We offer an optional premium tier with extra features like advanced filters and priority visibility, but the core experience costs nothing.",
  // },
  {
    q: "How does identity verification work?",
    a: "Every new account goes through a real-time verification process using document scanning and liveness detection. This ensures every person on the platform is exactly who they say they are. It takes under two minutes and keeps the community genuine.",
  },
  {
    q: "What are disappearing photos?",
    a: "When you share a photo in a conversation, it's visible for a limited time and then permanently deleted — from our servers and theirs. We also detect and alert you if a screenshot is attempted. Your images stay yours.",
  },
  {
    q: "Do you sell my data?",
    a: "Never. We don't sell, share, or monetise your personal data with third-party advertisers — period. You can request a full export or permanent deletion of your data at any time, processed within 48 hours.",
  },
  {
    q: "Can I hide my location?",
    a: "Absolutely. Location is always approximate — we show a general area, never your exact coordinates. You can also turn location off entirely from your settings at any time with no impact on other features.",
  },
  {
    q: "What if I encounter someone inappropriate?",
    a: "Report them with a single tap. Every report is reviewed by our safety team, with the most serious concerns prioritised first. Repeat violations result in suspension or permanent bans. Your safety is not negotiable.",
  },
  {
    q: "When is the app launching?",
    a: "We're putting the finishing touches on things right now. Drop your email on our waitlist and you'll be among the very first to get access — plus early adopter perks.",
  },
];

// ── Accordion item component
function FaqItem({ faq, itemRef }) {
  const [open, setOpen] = useState(false);
  const answerRef = useRef(null);
  const tlRef = useRef(null);

  const toggle = () => {
    if (!tlRef.current) {
      tlRef.current = gsap.timeline({ paused: true });
      tlRef.current.to(answerRef.current, {
        height: "auto",
        duration: 0.4,
        ease: "power2.inOut",
      });
    }

    if (!open) {
      tlRef.current.play();
    } else {
      tlRef.current.reverse();
    }

    setOpen((o) => !o);
  };

  return (
    <Item ref={itemRef}>
      <Question onClick={toggle}>
        <QuestionText open={open} className="q-text">
          {faq.q}
        </QuestionText>
        <IconWrap open={open} className="q-icon">
          <PlusIcon open={open} />
        </IconWrap>
      </Question>
      <AnswerWrap ref={answerRef}>
        <Answer>{faq.a}</Answer>
      </AnswerWrap>
    </Item>
  );
}

// ── Main component
export default function FAQSection() {
  const sectionRef = useRef(null);
  const eyebrowRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const itemRefs = useRef([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header
      gsap
        .timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 78%",
            end: "top 42%",
            scrub: 0.7,
          },
        })
        .to(eyebrowRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0)
        .to(titleRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.1)
        .to(subtitleRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.2);

      // Each FAQ item staggers in
      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap
          .timeline({
            scrollTrigger: {
              trigger: el,
              start: "top 90%",
              end: "top 65%",
              scrub: 0.6,
            },
          })
          .to(el, { opacity: 1, y: 0, ease: "power2.out" });
      });

      // Bottom note
      gsap
        .timeline({
          scrollTrigger: {
            trigger: bottomRef.current,
            start: "top 90%",
            end: "top 65%",
            scrub: 0.6,
          },
        })
        .to(bottomRef.current, { opacity: 1, y: 0, ease: "power2.out" });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section ref={sectionRef} id="faq">
      <Grain />
      <Orb s="500px" c="rgba(124,58,237,0.18)" top="0%" left="-8%" op={0.2} />
      <Orb
        s="350px"
        c="rgba(232,121,249,0.12)"
        top="60%"
        right="-6%"
        op={0.15}
      />

      <Container>
        <Header>
          <Eyebrow ref={eyebrowRef}>Got questions?</Eyebrow>
          <Title ref={titleRef}>
            We've got <em>answers.</em>
          </Title>
          <Subtitle ref={subtitleRef}>
            Everything you need to know before you swipe.
          </Subtitle>
        </Header>

        <List>
          {FAQS.map((faq, i) => (
            <FaqItem
              key={i}
              faq={faq}
              itemRef={(el) => (itemRefs.current[i] = el)}
            />
          ))}
        </List>

        <BottomNote ref={bottomRef}>
          <p>Still have questions?</p>
          <a href="mailto:hello@yourapp.com">Reach out to us →</a>
        </BottomNote>
      </Container>
    </Section>
  );
}
